'use client';

import { Container } from 'react-bootstrap';
import { useMedia } from 'react-use';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { breakpoints } from '@/app.config';
import CloseSVG from '@/icons/CloseSVG';

import { getMonthsFromDays } from '@/helpers/format-time.helpers';

import { PeriodEnum } from '@/enums/subscription-type.enums';

import { Button } from '@/components/Buttons';
import { Logo } from '@/components/Logo';
import { Modal, ModalProps } from '@/components/Modal';

import { useStore } from '@/contexts/StoreContext';
import { PeriodCard } from '@/contexts/VerificationContext/VerificationModal/SubscriptionVerification/PeriodCard';
import styles from '@/contexts/VerificationContext/VerificationModal/SubscriptionVerification/SubscriptionPeriod.module.scss';

// =================================================================

interface SubscriptionPeriodProps extends Omit<ModalProps, 'canOutsideClickClose' | 'children'> {
  onSubmit: () => void;
}

// =================================================================

export const SubscriptionPeriod = (props: SubscriptionPeriodProps) => {
  const { onClose, onSubmit, ...rest } = props;

  const t = useTranslations();

  const medium = useMedia(breakpoints.medium, false);

  const {
    selectToBuySubscription,
    subscriptionPeriod: subscriptionPeriodState,
    setSubscriptionPeriod,
  } = useStore(store => store.subscriptionStore);

  return (
    <Modal
      canOutsideClickClose={false}
      onClose={onClose}
      {...rest}
      className={styles.subscriptionPeriodModal}
      modalInnerContainerClassName={styles.subscriptionPeriodModalInner}
      fill
    >
      <Modal.Header
        isCloseButtonShown={false}
        className={clsx(styles.periodHeader, {
          [styles.mobile]: !medium,
        })}
        onClose={onClose}
      >
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <Logo className={styles.period} isHomeLink={false} />
            <Button
              as="button"
              size="md"
              variant="secondary"
              shape="circle"
              title={'Закрыть'}
              className={styles.closeButton}
              onClick={onClose}
            >
              <CloseSVG className={styles.closeButtonIcon} width={20} height={20} />
            </Button>
          </div>
        </Container>
      </Modal.Header>

      <div className={styles.subscriptionPeriodBody}>
        <h1 className={styles.title}>
          {t('page.userProfile.payment.chooseSubscription')}{' '}
          <span className={styles.title__highlighted}>
            {t('page.userProfile.payment.profitable')}
          </span>{' '}
          {t('page.userProfile.payment.subscription')}
        </h1>

        {selectToBuySubscription && (
          <div className={styles.periodCards}>
            <PeriodCard
              {...selectToBuySubscription.period[PeriodEnum.Month]}
              period={PeriodEnum.Month}
              isActive={subscriptionPeriodState === PeriodEnum.Month}
              onClick={() => setSubscriptionPeriod(PeriodEnum.Month)}
            />

            <PeriodCard
              {...selectToBuySubscription.period[PeriodEnum.Year]}
              period={PeriodEnum.Year}
              isActive={subscriptionPeriodState === PeriodEnum.Year}
              onClick={() => setSubscriptionPeriod(PeriodEnum.Year)}
            />
          </div>
        )}

        <p className={styles.description}>
          {t('page.userProfile.payment.onePeriodEquals', {
            period:
              getMonthsFromDays(
                selectToBuySubscription?.period[subscriptionPeriodState]?.days ?? 0,
              ) > 1
                ? t('common.year', {
                    count: 1,
                  })
                : t('common.month', {
                    count: 1,
                  }),
            days: selectToBuySubscription?.period[subscriptionPeriodState]?.days ?? 0,
          })}
        </p>

        <Button
          as="button"
          size="lg"
          variant="primary"
          className={styles.button}
          onClick={onSubmit}
        >
          {t('page.userProfile.payment.connect')}
        </Button>
      </div>
    </Modal>
  );
};
